import React from "react";
import styled, { keyframes } from "styled-components";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  CardSubtitle,
  CardFooter,
} from "../../../styles/components/ModernCard";
import {
  FaBuilding,
  FaFileAlt,
  FaClipboardCheck,
  FaUserGraduate,
} from "react-icons/fa";

// Pulse animation for icons
const iconPulse = keyframes`
  0% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.9; }
`;

// Subtle border glow animation
const borderGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.1), 0 0 5px rgba(99, 102, 241, 0.1) inset; }
  50% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), 0 0 10px rgba(99, 102, 241, 0.15) inset; }
  100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.1), 0 0 5px rgba(99, 102, 241, 0.1) inset; }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${(props) =>
    props.primary ? "rgba(79, 70, 229, 0.85)" : "rgba(15, 23, 42, 0.3)"};
  color: ${(props) => (props.primary ? "white" : props.theme.colors.primary)};
  border: ${(props) =>
    props.primary
      ? "1px solid rgba(99, 102, 241, 0.5)"
      : "1.5px solid rgba(99, 102, 241, 0.6)"};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  backdrop-filter: blur(8px);

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${(props) =>
        props.primary
          ? "rgba(129, 140, 248, 0.2) 0%, rgba(99, 102, 241, 0.2) 50%, rgba(79, 70, 229, 0.1) 100%"
          : "rgba(255, 255, 255, 0.1) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(15, 23, 42, 0.1) 100%"}
    );
    z-index: -1;
  }

  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: -100%;
    left: -100%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transition: all 0.6s ease;
    z-index: 1;
  }

  &:hover {
    background-color: ${(props) =>
      props.primary ? "rgba(79, 70, 229, 0.95)" : "rgba(99, 102, 241, 0.15)"};
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.primary
        ? "0 8px 25px -5px rgba(79, 70, 229, 0.5), 0 0 0 2px rgba(129, 140, 248, 0.3)"
        : "0 7px 20px -5px rgba(31, 38, 135, 0.3), 0 0 0 2px rgba(99, 102, 241, 0.1)"};

    &:after {
      top: 100%;
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1rem;
  }
`;

const ActivityItem = styled.div`
  padding: 0.85rem;
  border-radius: 10px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  transition: all 0.3s ease;
  background: rgba(15, 23, 42, 0.03);
  border: 1px solid rgba(148, 163, 184, 0.05);
  position: relative;

  /* Space-themed subtle glimmer effect */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(99, 102, 241, 0.02) 40%,
      rgba(124, 58, 237, 0.03) 60%,
      transparent 100%
    );
    z-index: -1;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  &:hover {
    transform: translateX(3px);
    background-color: rgba(15, 23, 42, 0.06);
    box-shadow: 0 3px 15px rgba(15, 23, 42, 0.05);
    border-color: rgba(99, 102, 241, 0.2);

    &:before {
      opacity: 1;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.12);
  color: rgba(99, 102, 241, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 15px rgba(99, 102, 241, 0.15);
  position: relative;
  overflow: hidden;
  animation: ${iconPulse} 4s infinite ease-in-out;

  &:before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      rgba(99, 102, 241, 0.1) 0%,
      transparent 60%
    );
    opacity: 0.5;
  }

  svg {
    font-size: 1.2rem;
    filter: drop-shadow(0 0 3px rgba(99, 102, 241, 0.4));
  }
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.25rem;
  letter-spacing: 0.3px;
`;

const ActivityTime = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.2px;
`;

// Helper function to get the appropriate icon
const getActivityIcon = (type) => {
  switch (type) {
    case "company":
      return <FaBuilding />;
    case "report":
      return <FaFileAlt />;
    case "approval":
      return <FaClipboardCheck />;
    case "student":
      return <FaUserGraduate />;
    default:
      return <FaFileAlt />;
  }
};

// Styled Card with space theme
const SpaceCard = styled(Card)`
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: ${borderGlow} 8s infinite ease-in-out;
  position: relative;
  z-index: 1;

  /* Space background gradients */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 20%,
        rgba(99, 102, 241, 0.05) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 80%,
        rgba(124, 58, 237, 0.03) 0%,
        transparent 50%
      );
    border-radius: inherit;
    z-index: -1;
  }

  /* Star-like dots in background */
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: radial-gradient(
        1px 1px at 15% 15%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 35% 45%,
        rgba(255, 255, 255, 0.6) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 55% 25%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 75% 65%,
        rgba(255, 255, 255, 0.6) 1px,
        transparent 0
      ),
      radial-gradient(
        1.5px 1.5px at 90% 10%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      );
    border-radius: inherit;
    z-index: -1;
    opacity: 0.3;
  }
`;

// Styled Card Header with space theme
const SpaceCardHeader = styled(CardHeader)`
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  padding: 1.25rem 1.5rem;
`;

// Styled Card Title with space theme
const SpaceCardTitle = styled(CardTitle)`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  letter-spacing: 0.5px;
  position: relative;
  display: inline-block;

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.7),
      rgba(124, 58, 237, 0.5)
    );
    border-radius: 2px;
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
  }
`;

// Styled Card Subtitle with space theme
const SpaceCardSubtitle = styled(CardSubtitle)`
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.3px;
`;

// Styled Card Footer with space theme
const SpaceCardFooter = styled(CardFooter)`
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  padding: 1.25rem 1.5rem;
`;

const ActivityList = ({ activities, date }) => {
  return (
    <SpaceCard>
      <SpaceCardHeader>
        <SpaceCardTitle>Recent Activities</SpaceCardTitle>
        <SpaceCardSubtitle>{date}</SpaceCardSubtitle>
      </SpaceCardHeader>{" "}
      <CardBody>
        {activities.map((activity, index) => (
          <ActivityItem key={index}>
            <ActivityIcon>{getActivityIcon(activity.type)}</ActivityIcon>
            <ActivityContent>
              <ActivityTitle>{activity.title}</ActivityTitle>
              <ActivityTime>{activity.time}</ActivityTime>
            </ActivityContent>
          </ActivityItem>
        ))}
      </CardBody>
    </SpaceCard>
  );
};

export default ActivityList;
